import { Controller } from "@hotwired/stimulus";
import { addToQueue, getAllQueue } from "../../pwa/offline_queue";
import { showPwaFlash } from "../../pwa/show_pwa_flash";

export default class extends Controller {
    static targets = [
        "video", "canvas", "preview", "cameraEmpty",
        "retakeButton", "shootButton", "saveButton",
        "photoType", "projectPart", "memo",
        "thumbs", "thumbsEmpty"
    ];

    static values = {
        uploadUrl: String,
        csrfToken: String,
        mode: String,
        workerIds: String,
        backUrl: String
    };

    connect() {
        this.lastImage = "";
        this.stream = null;
        this.saving = false;
        this.#restoreNavContext();
        this.#loadQueuedPhotos();
        this.startCamera();
        this.handleBeforeUnload = () => this.stopCamera();
        window.addEventListener("beforeunload", this.handleBeforeUnload);
    }

    disconnect() {
        this.stopCamera();
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
    }

    // When the page is reached offline, the cached HTML may have been rendered
    // for a different mode/worker_ids combination. Mode toggle stashes the real
    // context in sessionStorage; override the data values (and visible header)
    // here so uploads carry the right metadata.
    #restoreNavContext() {
        let storedMode = null;
        let storedWorkerIds = null;
        try {
            storedMode = sessionStorage.getItem("photo_nav_mode");
            storedWorkerIds = sessionStorage.getItem("photo_nav_worker_ids");
        } catch (e) { return; }

        if (storedMode) this.modeValue = storedMode;
        if (storedWorkerIds) this.workerIdsValue = storedWorkerIds;

        this.#updateHeaderMeta();
    }

    #updateHeaderMeta() {
        const meta = document.querySelector(".photo-meta");
        if (!meta) return;
        const spans = meta.querySelectorAll("span");
        if (spans.length < 3) return;

        const workers = this.workerIdList();
        const baseName = spans[1].textContent.split("：")[1]?.split(" 他")[0]?.trim() || "";
        if (this.modeValue === "group" && workers.length > 0) {
            spans[1].textContent = `対象：${baseName} 他${workers.length - 1}名`;
        } else {
            spans[1].textContent = `対象：${baseName}`;
        }
        spans[2].textContent = `モード：${this.modeValue === "group" ? "班一括" : "個人"}`;
    }

    async startCamera() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.showCameraError();
            return;
        }
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" } },
                audio: false
            });
            this.videoTarget.srcObject = this.stream;
        } catch (e) {
            console.error("Camera error:", e);
            this.showCameraError();
        }
    }

    stopCamera() {
        if (this.stream) {
            this.stream.getTracks().forEach(t => t.stop());
            this.stream = null;
        }
    }

    showCameraError() {
        this.videoTarget.hidden = true;
        if (this.hasCameraEmptyTarget) {
            this.cameraEmptyTarget.hidden = false;
        }
    }

    capture() {
        const video = this.videoTarget;
        if (!video.videoWidth) {
            alert("カメラ映像を取得中です。少し待ってから撮影してください。");
            return;
        }
        const canvas = this.canvasTarget;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        this.lastImage = canvas.toDataURL("image/jpeg", 0.86);

        this.previewTarget.src = this.lastImage;
        this.previewTarget.hidden = false;
        this.videoTarget.hidden = true;
        this.retakeButtonTarget.hidden = false;
    }

    retake() {
        this.lastImage = "";
        this.previewTarget.removeAttribute("src");
        this.previewTarget.hidden = true;
        this.videoTarget.hidden = false;
        this.retakeButtonTarget.hidden = true;
    }

    async save() {
        if (!this.lastImage) {
            alert("先に撮影してください。");
            return;
        }
        if (this.saving) return;

        this.saving = true;
        this.saveButtonTarget.disabled = true;

        // Keys mirror the multipart form encoding the online endpoint expects.
        // sync.js#buildFormData appends arrays under the literal key, so we use
        // "worker_ids[]" so Rails parses params[:worker_ids] as an Array.
        const payload = {
            image: this.lastImage,
            photo_type: this.photoTypeTarget.value,
            project_part_id: this.projectPartTarget.value,
            memo: this.memoTarget.value.trim(),
            mode: this.modeValue,
            "worker_ids[]": this.workerIdList()
        };

        try {
            if (!navigator.onLine) {
                await this.#saveOffline(payload);
                return;
            }

            const body = new FormData();
            body.append("image", payload.image);
            body.append("photo_type", payload.photo_type);
            body.append("project_part_id", payload.project_part_id);
            body.append("memo", payload.memo);
            body.append("mode", payload.mode);
            payload["worker_ids[]"].forEach(id => body.append("worker_ids[]", id));

            const res = await fetch(this.uploadUrlValue, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": this.csrfTokenValue,
                    "Accept": "application/json"
                },
                body
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || "保存に失敗しました。");
            }

            const data = await res.json();
            showPwaFlash(data.message || "写真を保存しました。", "success");
            this.appendThumb(data.photo);
            this.#resetForm();
        } catch (e) {
            // Network failure mid-request — fall back to offline queue rather
            // than discarding the photo the user already took.
            if (!navigator.onLine || e.name === "TypeError") {
                try {
                    await this.#saveOffline(payload);
                    return;
                } catch (queueErr) {
                    console.error(queueErr);
                }
            }
            console.error(e);
            alert(e.message || "保存に失敗しました。");
        } finally {
            this.saving = false;
            this.saveButtonTarget.disabled = false;
        }
    }

    async #saveOffline(payload) {
        await addToQueue({ type: "photo_upload", payload });
        showPwaFlash("オフラインで保存しました。オンライン復帰後に同期されます。", "success");
        this.appendQueuedThumb(payload);
        this.#resetForm();
    }

    #resetForm() {
        this.retake();
        this.memoTarget.value = "";
    }

    close() {
        this.stopCamera();
        if (this.hasBackUrlValue && this.backUrlValue) {
            window.location.href = this.backUrlValue;
        } else {
            window.history.back();
        }
    }

    workerIdList() {
        try {
            const parsed = JSON.parse(this.workerIdsValue || "[]");
            return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        } catch (e) {
            return [];
        }
    }

    async #loadQueuedPhotos() {
        try {
            const queue = await getAllQueue();
            const photos = queue.filter(item => item.type === "photo_upload");
            if (photos.length === 0) return;
            if (this.hasThumbsEmptyTarget) this.thumbsEmptyTarget.remove();
            photos.forEach(item => this.appendQueuedThumb(item.payload, { prepend: false }));
        } catch (e) {
            console.warn("Failed to load queued photos:", e);
        }
    }

    appendThumb(photo) {
        if (!photo || !this.hasThumbsTarget) return;
        if (this.hasThumbsEmptyTarget) {
            this.thumbsEmptyTarget.remove();
        }
        const wrap = document.createElement("div");
        wrap.className = "photo-thumb";

        const img = document.createElement("img");
        img.src = photo.url;
        img.alt = "保存済み写真";

        const meta = document.createElement("div");
        meta.className = "photo-thumb-meta";
        const parts = [photo.time];
        if (photo.photo_type_label) parts.push(photo.photo_type_label);
        if (photo.site) parts.push(photo.site);
        meta.innerHTML = parts.filter(Boolean).join("<br>");

        wrap.appendChild(img);
        wrap.appendChild(meta);
        this.thumbsTarget.insertBefore(wrap, this.thumbsTarget.firstChild);
    }

    appendQueuedThumb(payload, options = { prepend: true }) {
        if (!payload || !this.hasThumbsTarget) return;
        if (this.hasThumbsEmptyTarget) {
            this.thumbsEmptyTarget.remove();
        }
        const wrap = document.createElement("div");
        wrap.className = "photo-thumb photo-thumb-pending";

        const img = document.createElement("img");
        img.src = payload.image;
        img.alt = "同期待ちの写真";

        const meta = document.createElement("div");
        meta.className = "photo-thumb-meta";
        const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const parts = [time, "同期待ち"];
        if (payload.memo) parts.push(payload.memo);
        meta.innerHTML = parts.filter(Boolean).join("<br>");

        wrap.appendChild(img);
        wrap.appendChild(meta);
        if (options.prepend) {
            this.thumbsTarget.insertBefore(wrap, this.thumbsTarget.firstChild);
        } else {
            this.thumbsTarget.appendChild(wrap);
        }
    }
}
